import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { pipelineService } from '../services/pipelineService';


// UUID generator function
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

interface ProjectData {
  id: string;
  name: string;
  pillar: string;
  fundingAsk: string;
  leadCountry: string;
}

const ProjectMemo: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [memoContent, setMemoContent] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [projectData, setProjectData] = useState<ProjectData | null>(null);
  const [hasGenerated, setHasGenerated] = useState(false);

  const fmtMoney = (amount?: number) => {
    if (!amount) return 'N/A';
    if (amount >= 1e9) return `$${(amount / 1e9).toFixed(1)}B`;
    if (amount >= 1e6) return `$${(amount / 1e6).toFixed(0)}M`;
    return `$${amount.toLocaleString()}`;
  };

  // Fetch the real project for this route, then auto-generate the memo.
  useEffect(() => {
    let cancelled = false;
    const id = decodeURIComponent(projectId || '');

    const load = async () => {
      try {
        const project = await pipelineService.getProject(id);
        if (cancelled) return;
        const data: ProjectData = {
          id: project.id || id,
          name: project.name || id,
          pillar: project.pillar || 'N/A',
          fundingAsk: fmtMoney(project.investment_size),
          leadCountry: project.lead_country || 'N/A',
        };
        setProjectData(data);
        generateMemo(data);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading project:', err);
        // Graceful fallback: show the id rather than a fake name.
        const data: ProjectData = {
          id,
          name: id,
          pillar: 'N/A',
          fundingAsk: 'N/A',
          leadCountry: 'N/A',
        };
        setProjectData(data);
        generateMemo(data);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const generateMemo = async (project?: ProjectData) => {
    const target = project || projectData;
    if (!target) return;

    setIsGenerating(true);
    setError('');
    setMemoContent('');
    setHasGenerated(false);

    try {
      // Use API_URL from services/api to ensure HTTPS and authorization
      const baseUrl = (await import('../services/api')).API_URL;

      const response = await fetch(`${baseUrl}/agents/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          message: `Write a 500-word investment memo for ${target.name} (ID: ${target.id}, Sector: ${target.pillar}, Investment: ${target.fundingAsk}, Country: ${target.leadCountry}).

Include: Executive Summary, Strategic Rationale (3-4 bullets), Financial Overview, Regional Impact (2-3 bullets), Risk Considerations (2-3 risks), Recommendation.

Use formal business language, clear headings, bullet points. No emojis.`,
          conversation_id: generateUUID(),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to generate memo: ${response.status} - ${errorText}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');

          // Keep the last incomplete line in the buffer
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));

                // Stream tokens as they arrive (live typing effect)
                if (data.type === 'token' && data.content) {
                  setMemoContent(prev => prev + data.content);
                }
                // Authoritative full memo at the end — overwrites the streamed text
                else if (data.type === 'final_response' && data.content) {
                  setMemoContent(data.content);
                }
                // Legacy shape (kept for backward compatibility)
                else if (data.type === 'response' && data.message?.content) {
                  setMemoContent(data.message.content);
                }
                // Handle error event
                else if (data.type === 'error') {
                  setError(data.error || data.message || 'An error occurred');
                }
                // Handle done event
                else if (data.type === 'done') {
                  console.log('Memo generation completed');
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e, 'Line:', line);
              }
            }
          }
        }
      }
    } catch (err) {
      console.error('Error generating memo:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate memo. Please try again.';
      setError(errorMessage);
    } finally {
      setIsGenerating(false);
      setHasGenerated(true);
    }
  };

  const exportMemo = async () => {
    if (!memoContent) return;
    const pid = projectData?.id || projectId;
    try {
      const api = (await import('../services/api')).default;
      const res = await api.post(`/pipeline/${pid}/memo/pdf`, { content: memoContent }, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(projectData?.name || 'Project').replace(/\s+/g, '_')}_Investment_Memo.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to export memo PDF', e);
      alert('Failed to export memo as PDF. Please try again.');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(memoContent);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink-500)' }}>
        <button onClick={() => navigate('/dashboard')} className="qp-transition hover:opacity-70">
          Home
        </button>
        <span className="material-symbols-outlined text-[16px]">chevron_right</span>
        <button onClick={() => navigate('/deal-pipeline')} className="qp-transition hover:opacity-70">
          Deal Pipeline
        </button>
        <span className="material-symbols-outlined text-[16px]">chevron_right</span>
        <button
          onClick={() => navigate(`/deal-pipeline/${encodeURIComponent(projectData?.id || decodeURIComponent(projectId || ''))}`)}
          className="qp-transition hover:opacity-70"
        >
          {projectData?.name || 'Project'}
        </button>
        <span className="material-symbols-outlined text-[16px]">chevron_right</span>
        <span className="font-medium" style={{ color: 'var(--ink-900)' }}>Investment Memo</span>
      </div>

      {/* Page Header */}
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl md:text-4xl font-black leading-tight tracking-tight" style={{ color: 'var(--ink-900)' }}>
              Investment Memo
            </h1>
            {isGenerating && (
              <div className="flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium" style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>
                <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}></div>
                <span>AI Generating...</span>
              </div>
            )}
          </div>
          <p className="text-sm" style={{ color: 'var(--ink-500)' }}>
            AI-Generated Investment Analysis for {projectData?.name || 'this project'}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={copyToClipboard}
            disabled={!memoContent || isGenerating}
            className="clickable-scale qp-transition flex items-center gap-2 px-4 py-2 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink-700)', borderRadius: 'var(--radius-ctl)' }}
          >
            <span className="material-symbols-outlined text-[18px]">content_copy</span>
            Copy
          </button>
          <button
            onClick={exportMemo}
            disabled={!memoContent || isGenerating}
            className="clickable-scale qp-transition flex items-center gap-2 px-4 py-2 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink-700)', borderRadius: 'var(--radius-ctl)' }}
          >
            <span className="material-symbols-outlined text-[18px]">download</span>
            Export
          </button>
          <button
            onClick={() => generateMemo()}
            disabled={isGenerating}
            className="clickable-scale qp-transition flex items-center gap-2 px-4 py-2 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'var(--accent)', color: 'var(--accent-ink)', borderRadius: 'var(--radius-ctl)' }}
          >
            <span className="material-symbols-outlined text-[18px]">refresh</span>
            Regenerate
          </button>
        </div>
      </div>

      {/* AI Info Banner */}
      <div className="glass-martin p-4 flex items-start gap-4">
        <div className="p-2 shrink-0" style={{ background: 'var(--surface)', color: 'var(--accent)', borderRadius: 'var(--radius-ctl)' }}>
          <span className="material-symbols-outlined">auto_awesome</span>
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold" style={{ color: 'var(--ink-900)' }}>AI-Powered Analysis</h3>
          <p className="text-sm mt-1" style={{ color: 'var(--ink-600)' }}>
            This investment memo has been generated by our AI Supervisor Agent using advanced analysis of project
            data, market conditions, and regional development priorities.
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 flex items-start gap-3" style={{ background: 'color-mix(in srgb, var(--terra) 8%, var(--surface))', border: '1px solid color-mix(in srgb, var(--terra) 30%, var(--border))', borderRadius: 'var(--radius-card)' }}>
          <span className="material-symbols-outlined" style={{ color: 'var(--terra)' }}>error</span>
          <div>
            <h4 className="text-sm font-bold" style={{ color: 'var(--terra)' }}>Error Generating Memo</h4>
            <p className="text-sm mt-1" style={{ color: 'var(--ink-700)' }}>{error}</p>
          </div>
        </div>
      )}

      {/* Memo Content */}
      <div className="glass-card">
        <div className="p-6 md:p-8">
          {isGenerating && !memoContent && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mb-4" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}></div>
              <p className="font-medium" style={{ color: 'var(--ink-600)' }}>
                Generating investment memo with AI...
              </p>
              <p className="text-sm mt-2" style={{ color: 'var(--ink-500)' }}>
                This may take 30-60 seconds
              </p>
            </div>
          )}

          {memoContent && (
            <div className="prose max-w-none">
              <div className="whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--ink-800)' }}>
                {memoContent}
              </div>
            </div>
          )}

          {!isGenerating && !memoContent && !error && hasGenerated && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <span className="material-symbols-outlined text-6xl mb-4" style={{ color: 'var(--amber)' }}>
                error_outline
              </span>
              <p className="font-medium" style={{ color: 'var(--ink-700)' }}>
                The memo generator returned no content
              </p>
              <p className="text-sm mt-2 max-w-md" style={{ color: 'var(--ink-500)' }}>
                The AI completed without producing a memo. This is usually a temporary issue &mdash; try regenerating.
              </p>
              <button
                onClick={() => generateMemo()}
                className="clickable-scale qp-transition mt-4 flex items-center gap-2 px-4 py-2 text-sm font-bold"
                style={{ background: 'var(--accent)', color: 'var(--accent-ink)', borderRadius: 'var(--radius-ctl)' }}
              >
                <span className="material-symbols-outlined text-[18px]">refresh</span>
                Regenerate
              </button>
            </div>
          )}

          {!isGenerating && !memoContent && !error && !hasGenerated && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <span className="material-symbols-outlined text-6xl mb-4" style={{ color: 'var(--ink-300)' }}>
                description
              </span>
              <p className="font-medium" style={{ color: 'var(--ink-600)' }}>No memo generated yet</p>
              <button
                onClick={() => generateMemo()}
                className="clickable-scale qp-transition mt-4 flex items-center gap-2 px-4 py-2 text-sm font-bold"
                style={{ background: 'var(--accent)', color: 'var(--accent-ink)', borderRadius: 'var(--radius-ctl)' }}
              >
                <span className="material-symbols-outlined text-[18px]">auto_awesome</span>
                Generate Memo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectMemo;
